import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";

import messages from "../../../assets/locale/messages";
import UIStepper from "../../ui/UIStepper/UIStepper";
import {
  BasicInformation,
  DefineRolesAndShifts,
  ZonesAndLogisticalRequirements,
} from "./Steps";
import FormFooter from "./FormFooter";
import PublishEventModal from "./Modals/PublishEventModal";
import {
  COMPANY_PORTAL_FORM_VARIANT,
  getPortalApiErrorMessage,
} from "../../../utils/companyPortal/eventApi";
import { showErrorMsg } from "../../../utils/Helpers";
import { eventStatuses } from "../../../utils/Constants";

import "./_Form.scss";

const getEventImageUrl = (event) => event?.image_url || "";

const getEventName = (event) => event?.name || "";

const getStaffRequestedCount = (rolesPayload) =>
  (rolesPayload?.event_roles_attributes || []).reduce((total, role) => {
    const males = Number(role?.maximum_number_of_males || 0);
    const females = Number(role?.maximum_number_of_females || 0);
    return total + males + females;
  }, 0);

const CreateForm = ({
  variant,
  isEditMode,
  onSaveDraft,
  onFinish,
  rolesOptions,
  requirementsOptions,
  supervisorsOptions,
  eventTypeOptions,
  eventScheduleBounds,
  initialEvent,
  initialStep,
  requestsPortal,
  eventCompanyId,
  onPersistDraft,
  onPersistStepContinue,
  onFinishPublish,
  onCancel,
  draftLoading,
  nextLoading,
}) => {
  const lang = useSelector((state) => state.locale.lang);
  const creatEvent =
    messages[lang]?.events?.creatEvent ?? messages.en.events.creatEvent;
  const sharedUi = messages[lang]?.shared ?? messages.en.shared;

  const isCompanyPortal = variant === COMPANY_PORTAL_FORM_VARIANT;

  /**
   * Admin StepThree parity: only pending drafts show "publish" on the last step.
   * Edit upcoming/ongoing/past/cancelled → save only, no publish confirmation modal.
   */
  const companyPortalShowPublishModal = useMemo(() => {
    if (!isCompanyPortal) return false;
    if (!isEditMode) return true;
    return initialEvent?.status === eventStatuses.pending;
  }, [isCompanyPortal, isEditMode, initialEvent?.status]);

  /**
   * "Save As Draft" persists in-progress changes, so the user also needs a way
   * to leave the form without saving. Show Cancel for any event being edited —
   * whether reached via the /edit route (isEditMode) or resumed in the wizard
   * with an already-created event (initialEvent has a status).
   */
  const companyPortalShowCancel =
    isCompanyPortal &&
    !!onCancel &&
    (isEditMode || !!initialEvent?.status);

  /**
   * A published event (upcoming/ongoing/etc.) can't be a draft — the same
   * persist action is labelled "Save" instead of "Save As Draft".
   */
  const companyPortalSaveDraftLabel = useMemo(() => {
    if (!isCompanyPortal || !isEditMode) return undefined;
    if (!initialEvent?.status) return undefined;
    return initialEvent.status !== eventStatuses.pending
      ? creatEvent.save
      : undefined;
  }, [isCompanyPortal, isEditMode, initialEvent?.status, creatEvent.save]);

  const companyPortalLastStepPrimaryLabel = useMemo(() => {
    if (!isCompanyPortal) return undefined;
    return companyPortalShowPublishModal
      ? creatEvent.finishAndPublishEvent
      : creatEvent.finishEvent;
  }, [
    isCompanyPortal,
    companyPortalShowPublishModal,
    creatEvent.finishAndPublishEvent,
    creatEvent.finishEvent,
  ]);

  const steps = useMemo(
    () => [
      {
        id: "basic-info",
        label: creatEvent.basicInfo,
        Component: BasicInformation,
      },
      {
        id: "roles-shifts",
        label: creatEvent.stepTwo,
        Component: DefineRolesAndShifts,
      },
      {
        id: "zones-requirements",
        label: creatEvent.stepThree,
        Component: ZonesAndLogisticalRequirements,
      },
    ],
    [
      creatEvent.basicInfo,
      creatEvent.stepTwo,
      creatEvent.stepThree,
    ],
  );

  const [step, setStep] = useState(() =>
    Math.min(Math.max(initialStep, 0), steps.length - 1),
  );
  const [publishModalOpen, setPublishModalOpen] = useState(false);

  useLayoutEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [step]);
  const [pendingPublishPayload, setPendingPublishPayload] = useState(null);
  const [publishSummary, setPublishSummary] = useState({});
  const [publishSubmitting, setPublishSubmitting] = useState(false);
  /** Location boundary (meters) from Basic Info step — seeds paid-training radius */
  const [eventLocationBoundaryMeters, setEventLocationBoundaryMeters] =
    useState("");

  useEffect(() => {
    const r = initialEvent?.event_radius ?? initialEvent?.eventRadius;
    if (r == null || r === "") return;
    setEventLocationBoundaryMeters((prev) => (prev !== "" ? prev : String(r)));
  }, [initialEvent]);

  const stepSubmitRef = useRef({});
  const stepApisRef = useRef({});

  const registerStepApi = useCallback((index, api) => {
    if (api == null) {
      delete stepApisRef.current[index];
    } else {
      stepApisRef.current[index] = api;
    }
  }, []);

  const isFirstStep = step === 0;
  const isLastStep = step === steps.length - 1;
  const publishLoading = isCompanyPortal ? nextLoading : publishSubmitting;

  const buildPublishSummary = useCallback(() => {
    const basicSummary = stepApisRef.current[0]?.buildSummary?.() || {};
    const rolesPayload = stepApisRef.current[1]?.buildPayload?.() || {};

    return {
      name: basicSummary.name || getEventName(initialEvent),
      image_url: basicSummary.image_url || getEventImageUrl(initialEvent),
      staffRequested: getStaffRequestedCount(rolesPayload),
    };
  }, [initialEvent]);

  const handleBack = () => {
    stepApisRef.current[step]?.resetValidationState?.();
    setStep((currentStep) => Math.max(currentStep - 1, 0));
  };

  /** In edit mode all steps are already persisted — allow free navigation between them. */
  const handleStepJump = useCallback(
    (_step, index) => {
      if (!isEditMode || index === step) return;
      stepApisRef.current[step]?.resetValidationState?.();
      setStep(index);
    },
    [isEditMode, step],
  );

  // buildPayload may upload files (event logo, zone map) and reject when offline;
  // surface the error and resolve `null` so callers abort instead of failing silently.
  const buildStepPayload = async (api, options) => {
    try {
      return (await api?.buildPayload?.(options)) ?? {};
    } catch (e) {
      showErrorMsg(getPortalApiErrorMessage(e, sharedUi.generalErrMsg));
      return null;
    }
  };

  const handleDraftClick = async () => {
    if (isCompanyPortal) {
      const api = stepApisRef.current[step];
      if (
        typeof api?.validatePortalDraft === "function" &&
        !(await api.validatePortalDraft())
      ) {
        return;
      }
      const payload = await buildStepPayload(api, { mode: "draft" });
      if (!payload) return;
      const persisted = await onPersistDraft?.({ stepIndex: step, payload });
      api?.applyPersistedEvent?.(persisted?.attributes);
      return;
    }
    await onSaveDraft?.();
  };

  const handleNext = async () => {
    if (isCompanyPortal) {
      const api = stepApisRef.current[step];
      if (!api?.validateContinue || !(await api.validateContinue())) {
        return;
      }
      const payload = await buildStepPayload(api);
      if (!payload) return;
      if (isLastStep) {
        if (companyPortalShowPublishModal) {
          setPendingPublishPayload(payload);
          setPublishSummary(buildPublishSummary());
          setPublishModalOpen(true);
          return;
        }
        await onFinishPublish?.({ stepIndex: step, payload });
        return;
      }
      const persisted = await onPersistStepContinue?.({
        stepIndex: step,
        payload,
      });
      if (!persisted) return;
      api?.applyPersistedEvent?.(persisted?.attributes);
      setStep((currentStep) => Math.min(currentStep + 1, steps.length - 1));
      return;
    }

    const validate = stepSubmitRef.current[step];
    if (validate) {
      const canContinue = await validate();
      if (!canContinue) return;
    }

    if (isLastStep) {
      setPublishSummary(buildPublishSummary());
      setPublishModalOpen(true);
      return;
    }

    setStep((currentStep) => Math.min(currentStep + 1, steps.length - 1));
  };

  const handlePublishConfirm = async () => {
    if (isCompanyPortal) {
      // Always close the modal here. On failure the parent shows an error and keeps this route;
      // on success the parent navigates to the events list (after this `finally` runs).
      try {
        const payload =
          pendingPublishPayload ||
          (await buildStepPayload(stepApisRef.current[step]));
        if (!payload) return;
        await onFinishPublish?.({ stepIndex: step, payload });
      } finally {
        setPublishModalOpen(false);
        setPendingPublishPayload(null);
      }
      return;
    }

    setPublishSubmitting(true);
    try {
      await onFinish?.();
      setPublishModalOpen(false);
    } finally {
      setPublishSubmitting(false);
    }
  };

  return (
    <div className="event-form-container">
      <UIStepper
        steps={steps}
        activeStep={step}
        onStepClick={isEditMode ? handleStepJump : undefined}
      />

      {steps.map((s, stepIndex) => {
        const StepComponent = s.Component;
        const hidden = step !== stepIndex;
        return (
          <div
            key={s.id}
            className="event-form-step-pane"
            hidden={hidden}
            aria-hidden={hidden}
          >
            <StepComponent
              isEditMode={isEditMode}
              rolesOptions={rolesOptions}
              requirementsOptions={requirementsOptions}
              supervisorsOptions={supervisorsOptions}
              eventTypeOptions={eventTypeOptions}
              companyPortalMode={isCompanyPortal}
              eventScheduleBounds={eventScheduleBounds}
              initialEvent={initialEvent}
              requestsPortal={requestsPortal}
              eventCompanyId={eventCompanyId}
              onEventLocationBoundarySync={setEventLocationBoundaryMeters}
              eventLocationBoundaryMeters={eventLocationBoundaryMeters}
              registerSubmit={
                isCompanyPortal
                  ? undefined
                  : (handler) => {
                      if (handler) {
                        stepSubmitRef.current[stepIndex] = handler;
                      } else {
                        delete stepSubmitRef.current[stepIndex];
                      }
                    }
              }
              registerStepApi={
                isCompanyPortal
                  ? (api) => registerStepApi(stepIndex, api)
                  : undefined
              }
            />
          </div>
        );
      })}

      <FormFooter
        isFirstStep={isFirstStep}
        isLastStep={isLastStep}
        stepName={steps[step + 1]?.label ?? ""}
        backLabel={sharedUi.back}
        onBack={handleBack}
        onSaveDraft={handleDraftClick}
        onNext={handleNext}
        saveDraftLoading={isCompanyPortal ? draftLoading : false}
        nextLoading={isCompanyPortal ? nextLoading : false}
        lastStepPrimaryLabel={companyPortalLastStepPrimaryLabel}
        saveDraftLabel={companyPortalSaveDraftLabel}
        showSaveDraft={!(isCompanyPortal && isLastStep)}
        onCancel={companyPortalShowCancel ? onCancel : undefined}
        cancelLabel={creatEvent.discardChanges}
      />
      <PublishEventModal
        open={publishModalOpen}
        onClose={() => {
          if (!publishLoading) {
            setPublishModalOpen(false);
          }
        }}
        onPublish={handlePublishConfirm}
        loading={publishLoading}
        eventSummary={publishSummary}
      />
    </div>
  );
};

export default CreateForm;

CreateForm.propTypes = {
  variant: PropTypes.string,
  isEditMode: PropTypes.bool,
  onSaveDraft: PropTypes.func,
  onFinish: PropTypes.func,
  rolesOptions: PropTypes.arrayOf(PropTypes.object),
  requirementsOptions: PropTypes.arrayOf(PropTypes.object),
  supervisorsOptions: PropTypes.arrayOf(PropTypes.object),
  eventTypeOptions: PropTypes.arrayOf(PropTypes.object),
  eventScheduleBounds: PropTypes.shape({
    eventStartAt: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    eventEndAt: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  initialEvent: PropTypes.object,
  initialStep: PropTypes.number,
  requestsPortal: PropTypes.oneOf(["admin", "company"]),
  eventCompanyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onPersistDraft: PropTypes.func,
  onPersistStepContinue: PropTypes.func,
  onFinishPublish: PropTypes.func,
  onCancel: PropTypes.func,
  draftLoading: PropTypes.bool,
  nextLoading: PropTypes.bool,
};

CreateForm.defaultProps = {
  variant: "default",
  isEditMode: false,
  onSaveDraft: undefined,
  onFinish: undefined,
  rolesOptions: [],
  requirementsOptions: [],
  supervisorsOptions: [],
  eventTypeOptions: undefined,
  eventScheduleBounds: undefined,
  initialEvent: undefined,
  initialStep: 0,
  requestsPortal: "admin",
  eventCompanyId: undefined,
  onPersistDraft: undefined,
  onPersistStepContinue: undefined,
  onFinishPublish: undefined,
  onCancel: undefined,
  draftLoading: false,
  nextLoading: false,
};
